import { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LoadingIndicator } from '@dhl/components'
import { ConfirmationDialog } from '@dhl/confirmation-dialog'
import { useKeycloakTokens } from '@dhl/keycloak'
import {
  useCreateAuthToken,
  useDeleteAddressBookP1,
  useDeleteParcelShopForUser,
  useDeleteUser,
  useDeleteUserFromOrganisation,
  useDeleteUserFromTerminalOrganisation,
} from '@dhl/mdp-api'
import { Alert, Body1, Grid } from '@dhl-ui/components'

import { links } from '~/AppRoutes'
import { useNotificationMessage } from '~/common'
import { getLoginRedirectUrl } from '~/modules/users/components/util'
import { User } from '~/modules/users/model'

export type ActionType = 'deleteAccount' | 'cleanAddressBook' | 'disconnectUser' | 'loginAsUser'

type Props = {
  user: User
  open: boolean
  actionType: ActionType
  onClose: () => void
  onUserRemoved?: (email: string) => void
  onUserDisconnected?: (email: string) => void
  warningMessage?: string
}

export const UsersDangerZoneDialog = ({
  open,
  user,
  actionType,
  onClose,
  onUserRemoved,
  onUserDisconnected,
  warningMessage,
}: Props) => {
  const { id: userId, email, userType } = user
  const navigate = useNavigate()
  const { showError, showSuccess } = useNotificationMessage()
  const { mutateAsync: deleteAddressBook } = useDeleteAddressBookP1()
  const { tokenParsed } = useKeycloakTokens()
  const { t } = useTranslation()
  const adminId = tokenParsed?.sub
  const [authToken, setAuthToken] = useState<string | undefined>(undefined)
  const [authUrl, setAuthUrl] = useState<string | undefined>(undefined)
  const isLoadingAuthToken = !authToken && actionType === 'loginAsUser'

  const { mutateAsync: createAuthToken } = useCreateAuthToken()
  const { mutateAsync: deleteUser } = useDeleteUser()

  const { mutateAsync: deleteParcelShopForUser } = useDeleteParcelShopForUser()
  const { mutateAsync: deleteUserFromOrganisation } = useDeleteUserFromOrganisation()
  const { mutateAsync: deleteUserFromTerminalOrganisation } = useDeleteUserFromTerminalOrganisation()

  const maybeShowError = (error: unknown) => {
    if (isAxiosError(error)) {
      showError(error.message)
    }
  }

  useEffect(() => {
    if (actionType === 'loginAsUser' && adminId) {
      createAuthToken(
        {
          data: {
            adminId,
            userId: String(userId),
          },
        },
        {
          onSuccess: ({ token, url }) => {
            if (token && url) {
              setAuthToken(token)
              setAuthUrl(url)
            }
          },
          onError: (error) => {
            maybeShowError(error)
          },
        }
      )
    }
  }, [actionType, adminId, userId])

  const openURLInNewTab = (url: string) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  const loginWithAuthToken = async () => {
    if (authUrl) {
      const { location: loc } = window
      const loginRedirectUrl = getLoginRedirectUrl(user, authUrl, loc)
      if ('redirectUrl' in loginRedirectUrl) {
        openURLInNewTab(loginRedirectUrl.redirectUrl)
      } else {
        showError(loginRedirectUrl.errorMessage)
      }
      onClose()
    }
  }

  const deleteUserFromServer = () =>
    deleteUser(
      { emailOrId: userId },
      {
        onSuccess: () => {
          showSuccess(t('admin.usersPage.dangerZoneView.buttonDeleteTitle'))
          onUserRemoved && onUserRemoved(email)
        },
        onError: (error) => {
          maybeShowError(error)
        },
      }
    )

  const clearAddressBookFromServer = () =>
    deleteAddressBook(
      { p1: userId },
      {
        onSuccess: () => {
          showSuccess(t('admin.usersPage.dangerZoneView.buttonBookTitle'))
          onClose()
        },
        onError: (error) => {
          maybeShowError(error)
        },
      }
    )

  const disconnectUserFromServer = async () => {
    try {
      const params = {
        emailOrId: userId,
      }
      switch (userType) {
        case 'parcelShop':
          await deleteParcelShopForUser(params)
          break
        case 'business':
          await deleteUserFromOrganisation(params)
          break
        case 'terminal':
          await deleteUserFromTerminalOrganisation(params)
          break
        default:
          return
      }
      onUserDisconnected?.(email)
      navigate(links.userDetailLink(userId))
      showSuccess(t('admin.usersPage.dangerZoneView.disconnectSuccess'))
      onClose()
    } catch (error) {
      maybeShowError(error)
      onClose()
    }
  }

  const handleDelete = async () => {
    switch (actionType) {
      case 'deleteAccount':
        await deleteUserFromServer()
        break
      case 'cleanAddressBook':
        await clearAddressBookFromServer()
        break
      case 'disconnectUser':
        await disconnectUserFromServer()
        break
      default:
        await loginWithAuthToken()
        break
    }
  }

  if (!userId || !email || !userType) {
    return null
  }

  return (
    <ConfirmationDialog
      isAcceptButtonDisabled={isLoadingAuthToken}
      open={open}
      titleText={t(`admin.usersPage.popUpView.dialogHeaderText`)}
      acceptText={
        actionType === 'deleteAccount'
          ? t('admin.usersPage.popUpView.deleteAccountButton')
          : actionType === 'cleanAddressBook'
            ? t('admin.usersPage.popUpView.cleanAddressBook')
            : actionType === 'disconnectUser'
              ? t('admin.usersPage.popUpView.disconnectUserButton')
              : t('admin.usersPage.popUpView.login')
      }
      rejectText={t(`admin.usersPage.popUpView.buttonCancel`)}
      onAccept={handleDelete}
      onReject={onClose}
    >
      {isLoadingAuthToken ? (
        <LoadingIndicator isFullHeight={false} />
      ) : (
        <Grid container>
          <Body1>
            {actionType === 'deleteAccount'
              ? t('admin.usersPage.popUpView.deleteAccountText')
              : actionType === 'cleanAddressBook'
                ? t('admin.usersPage.popUpView.dialogTitleText')
                : actionType === 'disconnectUser'
                  ? t('admin.usersPage.popUpView.disconnectUserTest')
                  : t('admin.usersPage.popUpView.loginText')}
          </Body1>
          {warningMessage && (
            <Grid container sx={{ mt: 2 }}>
              <Alert severity="warning">{warningMessage}</Alert>
            </Grid>
          )}
        </Grid>
      )}
    </ConfirmationDialog>
  )
}
